// Platform i18n entry point.
//
// Public API for consumers (steps, Puck blocks, any component, any site):
//
//   import { useTranslation } from 'i18n'
//   const { t } = useTranslation()          // 'common' namespace
//   const { t } = useTranslation('poll')    // a specific namespace
//   t('next')
//   t('counter', { current: 1, total: 3 })
//
// Everything else (which locale is active, how catalogs load, fallbacks) is
// internal and can change without touching consumers.

import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import { resources, DEFAULT_NS, NAMESPACES } from './resources'
import { FALLBACK_LNG, DEFAULT_LOCALE } from './locales'
import { resolveLocale } from './resolveLocale'

let initialized = false

export function initI18n() {
  if (initialized) return i18next
  initialized = true

  i18next.use(initReactI18next).init({
    resources,
    lng: resolveLocale(),
    fallbackLng: FALLBACK_LNG,
    supportedLngs: Object.keys(resources),
    ns: NAMESPACES,
    defaultNS: DEFAULT_NS,
    interpolation: {
      // React already escapes values.
      escapeValue: false,
    },
    // Keys are semantic (common.next), never English sentences, so a missing
    // key should be loud in dev rather than silently rendering the key path.
    debug: false,
    returnEmptyString: false,
  })

  return i18next
}

// Initialize on import. The theme's gatsby-ssr has no wrapRootElement, so
// relying on a mounted provider would leave SSR without translations; an
// idempotent eager init guarantees any consumer (browser or SSR) gets a ready
// instance. I18nProvider is still mounted for explicitness/future overrides.
initI18n()

export { i18next, DEFAULT_LOCALE }
export { resolveLocale } from './resolveLocale'
export { LOCALES, SUPPORTED_CODES } from './locales'

// Re-exported so consumers depend on our module, not react-i18next directly.
export { useTranslation, Trans } from 'react-i18next'

// For non-React call sites (helpers, services). Prefer useTranslation in components.
export const t = i18next.t.bind(i18next)
